import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const faqs = [
  {
    id: "different-from-form-builder",
    question: "How is this different from a $200/month form builder?",
    answer: "A $200 tool collects data. We capture revenue. Our AI answers your phone at 2 AM, qualifies the case, and books the consultation. A form builder just sits there hoping someone fills it out. We're a complete intake department, not a software subscription."
  },
  {
    id: "hipaa-compliant",
    question: "Is this really HIPAA compliant?",
    answer: "Yes. All data is encrypted at rest (AES-256) and in transit (TLS 1.2+). We sign a Business Associate Agreement (BAA) with every client before any Protected Health Information is processed. We maintain comprehensive audit logs of all PHI access. We can provide compliance documentation on request."
  },
  {
    id: "existing-forms",
    question: "What if I already have intake forms?",
    answer: "We'll analyze what's working and build something better. Mobile-optimized, AI-backed intake is built to convert significantly better than a static form sitting on a webpage. We'll show you exactly where your current setup is losing leads and how this closes that gap."
  },
  {
    id: "go-live-speed",
    question: "How fast can I be live?",
    answer: "Solo firms: 7 days. Larger firms: 10-14 days. The timeline depends on your complexity, not our capability."
  },
  {
    id: "ai-mistakes",
    question: "What if the AI makes a mistake?",
    answer: "The AI is trained specifically for PI intake. It qualifies leads based on your criteria and transfers hot leads to you immediately. If it's unsure, it takes a message and alerts you. You're always in control. The AI never gives legal advice and always offers a live option to reach a human immediately. If anything is unclear, it takes a message and alerts you right away - you're always in the loop."
  },
  {
    id: "cancel-policy",
    question: "Can I cancel?",
    answer: "Yes. Month-to-month after setup. 30 days notice. No hard feelings. We export all your data. We earn your business every month."
  }
];

const FAQItem = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover-lift"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <h3 className="text-lg font-bold text-white pr-4">{faq.question}</h3>
        <div className="text-emerald-400 text-xl flex-shrink-0">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-gray-300 leading-relaxed">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" id="faq">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-xl text-gray-400">
            Everything you need to know before getting started
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem key={faq.id} faq={faq} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
